/**
 * The single procedural FX atlas. ONE 1024² canvas texture feeds every particle, decal and
 * pop-text glyph in the game, so the whole subsystem costs a handful of draw calls and one
 * texture unit.
 *
 * ARCHITECTURE N1: no external assets. Everything below is drawn with canvas 2D at boot.
 * ARCHITECTURE §7.3: generated from a locally-seeded Rng (never ctx.rng, never Math.random)
 * so the bytes are identical on every run regardless of what else drew from the game stream.
 *
 * Layout (canvas coordinates, y down):
 *   y   0..384   sprite cells, 8 columns × 3 rows of 128 px
 *   y 384..1024  glyph cells, 16 columns × 10 rows of 64 px
 * (Row 3 of the sprite grid was added in round 5 for the contact shadow, the wheel-spin
 *  blur and the full-frame speed streak. The charset is 54 glyphs, so 10 rows of 16 is
 *  still nearly three times the space it needs.)
 *
 * Channel convention:
 *   RGB carries *shape shading* (white = takes the particle tint, black = stays black).
 *   A   carries coverage.
 * That is why pop-text works: the outline is drawn black (tint × 0 = black, always readable)
 * and the fill white (tint × 1 = the tint), from a single instanced quad with no extra pass.
 */

import * as THREE from 'three';
import { Rng } from '../core/rng.js';
import { FX } from './tuning.js';

export const SPRITE = Object.freeze({
  PUFF: 0, SMOKE: 1, CHUNK_A: 2, CHUNK_B: 3, CHUNK_C: 4, STREAK: 5, DROP: 6, RING: 7,
  SPLAT_A: 8, SPLAT_B: 9, SPLAT_C: 10, TYRE: 11, SMUDGE: 12, DOT: 13, SPEEDLINES: 14, WHITE: 15,
  /** round 5 — grounding + motion */
  CONTACT: 16, SPIN: 17, SPEEDSTREAK: 18,
  /** round 9 — angular fractured-asphalt debris (see FX.debris) */
  SHARD_A: 19, SHARD_B: 20,
  /** round 10 — the hero vehicle's FOOTPRINT shadow (see FX.contact.footprint*) */
  FOOTPRINT: 21,
  /**
   * ROUND 12 — the last two free cells in the sprite grid, both spent on the same defect:
   * "hard-edged white polygon needles with no alpha falloff" (night-rain wheel spray) and
   * "straight 1-px gold lines with no glow, no bloom, no falloff" (street-speed sparks).
   * DROPLET is a tapering comet for thrown water; SPARK is a hot core inside its own baked
   * glow, because `look` owns post and we have no bloom pass of our own to lean on.
   */
  DROPLET: 22, SPARK: 23,
});

/**
 * Sprite cells actually painted. The grid is 8 x 3 of 128 px and every cell is now used —
 * a fourth row would start at y 384, which is `A.glyphTop`. Adding a 25th sprite means
 * moving the glyph block down first.
 */
export const SPRITE_COUNT = 24;

/**
 * ROUND 8 MINOR — "airborne debris is a set of repeated identical rounded pebble meshes".
 * Three roundish blobs became five families: the three water-worn aggregate pebbles plus two
 * ANGULAR shards with straight fracture faces, which is what comes out of a saw-cut edge.
 */
export const CHUNKS = [SPRITE.CHUNK_A, SPRITE.CHUNK_B, SPRITE.CHUNK_C, SPRITE.SHARD_A, SPRITE.SHARD_B];
export const SPLATS = [SPRITE.SPLAT_A, SPRITE.SPLAT_B, SPRITE.SPLAT_C];

const A = FX.atlas;

export class FxAtlas {
  constructor() {
    this.size = A.size;
    /** Float32Array of [u0, v0, du, dv] per sprite index. */
    this.spriteRects = new Float32Array(SPRITE_COUNT * 4);
    /** char code → glyph slot index (into glyphRects / glyphAdv). */
    this.glyphIndex = new Int16Array(1024).fill(-1);
    this.glyphRects = null;
    this.glyphAdv = null;
    this.glyphCount = 0;
    this.texture = null;
    this.canvas = null;
  }

  build() {
    const S = this.size;
    const canvas = document.createElement('canvas');
    canvas.width = S; canvas.height = S;
    const g = canvas.getContext('2d', { alpha: true, willReadFrequently: false });
    g.clearRect(0, 0, S, S);

    const rng = new Rng('ppm.fx.atlas.v1');

    const cell = A.spriteCell;
    for (let i = 0; i < SPRITE_COUNT; i++) {
      const col = i % 8, row = (i / 8) | 0;
      const x = col * cell, y = row * cell;
      g.save();
      g.beginPath();
      g.rect(x, y, cell, cell);
      g.clip();
      g.translate(x, y);
      drawSprite(g, i, cell, rng);
      g.restore();
      // 1.5 px inset so bilinear filtering never bleeds a neighbouring cell in.
      const pad = 1.5;
      this._setRect(this.spriteRects, i, x + pad, y + pad, cell - pad * 2, cell - pad * 2);
    }

    this._buildGlyphs(g);

    const tex = new THREE.CanvasTexture(canvas);
    tex.name = 'fx.atlas';
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.generateMipmaps = true;
    tex.anisotropy = 4;
    tex.needsUpdate = true;

    this.texture = tex;
    this.canvas = canvas;
    return this;
  }

  /** Canvas rect (y down) → uv rect for a flipY texture. */
  _setRect(arr, i, x, y, w, h) {
    const S = this.size;
    arr[i * 4 + 0] = x / S;
    arr[i * 4 + 1] = 1 - (y + h) / S;
    arr[i * 4 + 2] = w / S;
    arr[i * 4 + 3] = h / S;
  }

  _buildGlyphs(g) {
    const chars = A.charset;
    const cell = A.glyphCell;
    const cols = (this.size / cell) | 0;
    const n = chars.length;
    this.glyphRects = new Float32Array(n * 4);
    this.glyphAdv = new Float32Array(n);
    this.glyphCount = n;

    g.save();
    g.font = A.glyphFont;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    g.miterLimit = 2;

    for (let i = 0; i < n; i++) {
      const ch = chars[i];
      const col = i % cols;
      const row = (i / cols) | 0;
      const x = col * cell;
      const y = A.glyphTop + row * cell;
      if (y + cell > this.size) break;

      const w = g.measureText(ch).width;
      const xs = w > A.glyphMaxWidth ? A.glyphMaxWidth / w : 1;

      if (ch !== ' ') {
        g.save();
        g.translate(x + cell * 0.5, y + cell * 0.5 + 1);
        g.scale(xs, 1);
        // Outline first (stays black under any tint), then the white fill (takes the tint).
        g.lineWidth = A.glyphOutlinePx / xs;
        g.strokeStyle = '#000000';
        g.strokeText(ch, 0, 0);
        g.fillStyle = '#ffffff';
        g.fillText(ch, 0, 0);
        g.restore();
      }

      this._setRect(this.glyphRects, i, x + 0.5, y + 0.5, cell - 1, cell - 1);
      // Advance in cell units. The glyph is centred in its cell, so spacing centres by the
      // advance lays out correctly whatever the quad size is.
      const drawn = (ch === ' ' ? A.glyphFontPx * 0.34 : w * xs);
      this.glyphAdv[i] = (drawn + A.glyphOutlinePx * 0.55) / cell;
      const code = ch.charCodeAt(0);
      if (code < 1024) this.glyphIndex[code] = i;
    }
    g.restore();
  }

  /** char code → glyph slot, with cheap uppercase folding. -1 if unmapped. */
  glyphFor(code) {
    if (code >= 97 && code <= 122) code -= 32;      // a-z → A-Z
    if (code >= 1024) return -1;
    return this.glyphIndex[code];
  }

  dispose() {
    if (this.texture) this.texture.dispose();
    this.texture = null;
    this.canvas = null;
  }
}

// -------------------------------------------------------------------- sprite painters

function drawSprite(g, id, c, rng) {
  switch (id) {
    case SPRITE.PUFF: return paintPuff(g, c);
    case SPRITE.SMOKE: return paintSmoke(g, c, rng);
    case SPRITE.CHUNK_A: return paintChunk(g, c, rng, 7, 0.86);
    case SPRITE.CHUNK_B: return paintChunk(g, c, rng, 5, 0.72);
    case SPRITE.CHUNK_C: return paintChunk(g, c, rng, 9, 0.94);
    case SPRITE.STREAK: return paintStreak(g, c);
    case SPRITE.DROP: return paintDrop(g, c);
    case SPRITE.RING: return paintRing(g, c);
    case SPRITE.SPLAT_A: return paintSplat(g, c, rng, 9, 6);
    case SPRITE.SPLAT_B: return paintSplat(g, c, rng, 7, 10);
    case SPRITE.SPLAT_C: return paintSplat(g, c, rng, 11, 3);
    case SPRITE.TYRE: return paintTyre(g, c, rng);
    case SPRITE.SMUDGE: return paintSmudge(g, c, rng);
    case SPRITE.DOT: return paintDot(g, c);
    case SPRITE.SPEEDLINES: return paintSpeedLines(g, c, rng);
    case SPRITE.CONTACT: return paintContact(g, c, rng);
    case SPRITE.FOOTPRINT: return paintFootprint(g, c, rng);
    case SPRITE.SPIN: return paintSpin(g, c, rng);
    case SPRITE.SPEEDSTREAK: return paintSpeedStreak(g, c, rng);
    case SPRITE.SHARD_A: return paintShard(g, c, rng, 4, 0.86);
    case SPRITE.SHARD_B: return paintShard(g, c, rng, 5, 0.70);
    case SPRITE.DROPLET: return paintDroplet(g, c, rng);
    case SPRITE.SPARK: return paintSpark(g, c);
    case SPRITE.WHITE: {
      g.fillStyle = '#ffffff';
      g.fillRect(0, 0, c, c);
      return;
    }
    default: return;   // spare cells stay fully transparent
  }
}

function radial(g, cx, cy, r, stops) {
  const grd = g.createRadialGradient(cx, cy, 0, cx, cy, r);
  for (const [t, col] of stops) grd.addColorStop(t, col);
  return grd;
}

function paintPuff(g, c) {
  const h = c * 0.5;
  g.fillStyle = radial(g, h, h, h * 0.98, [
    [0.0, 'rgba(255,255,255,1)'],
    [0.28, 'rgba(255,255,255,0.86)'],
    [0.60, 'rgba(255,255,255,0.34)'],
    [0.86, 'rgba(255,255,255,0.07)'],
    [1.0, 'rgba(255,255,255,0)'],
  ]);
  g.beginPath(); g.arc(h, h, h, 0, Math.PI * 2); g.fill();
}

function paintSmoke(g, c, rng) {
  const h = c * 0.5;
  // A handful of overlapping soft lobes gives a billowy silhouette, not a perfect circle.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + rng.range(-0.3, 0.3);
    const d = i === 0 ? 0 : h * rng.range(0.16, 0.34);
    const r = h * rng.range(0.42, 0.72);
    const cx = h + Math.cos(a) * d, cy = h + Math.sin(a) * d;
    g.fillStyle = radial(g, cx, cy, r, [
      [0.0, 'rgba(255,255,255,0.55)'],
      [0.55, 'rgba(255,255,255,0.24)'],
      [1.0, 'rgba(255,255,255,0)'],
    ]);
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill();
  }
}

/** Irregular rock silhouette with aggregate speckle and a top-left gloss. */
function paintChunk(g, c, rng, sides, fill) {
  const h = c * 0.5;
  const R = h * 0.9 * fill;
  g.beginPath();
  for (let i = 0; i <= sides; i++) {
    const a = (i / sides) * Math.PI * 2;
    const r = R * rng.range(0.62, 1.0);
    const x = h + Math.cos(a) * r, y = h + Math.sin(a) * r * 0.92;
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.closePath();
  g.fillStyle = '#ffffff';
  g.fill();

  // Everything below is clipped to the silhouette so alpha stays a clean cut-out.
  g.save();
  g.globalCompositeOperation = 'source-atop';

  // Bottom-shadow → the chunk reads as volumetric even flat-shaded.
  //
  // ROUND 4 FATAL. These stops used to bottom out at rgba(40,40,40,0.85), i.e. sRGB 72 —
  // which is linear 0.065, and the shader MULTIPLIES it by the particle albedo. A dark
  // asphalt albedo times 0.065 is RGB 0 on screen no matter how the frame is lit, which is
  // exactly what the critic sampled. The shading floor is now sRGB ~140 (linear 0.26), so
  // the form still reads but the darkest pixel of a chunk can never crush to black.
  g.fillStyle = radial(g, h * 0.72, h * 0.62, R * 1.5, [
    [0.0, 'rgba(255,255,255,0)'],
    [0.55, 'rgba(170,170,170,0.35)'],
    [1.0, 'rgba(120,120,120,0.85)'],
  ]);
  g.fillRect(0, 0, c, c);

  // Aggregate: the pale stone in the bitumen. Reads as texture at 40 px on screen.
  const n = 26 + ((rng.next() * 14) | 0);
  for (let i = 0; i < n; i++) {
    const a = rng.range(0, Math.PI * 2);
    const d = Math.sqrt(rng.next()) * R * 0.92;
    const rr = rng.range(c * 0.012, c * 0.045);
    const v = (rng.range(0.55, 1.0) * 255) | 0;
    g.fillStyle = `rgba(${v},${(v * 0.97) | 0},${(v * 0.92) | 0},${rng.range(0.45, 0.95).toFixed(3)})`;
    g.beginPath(); g.arc(h + Math.cos(a) * d, h + Math.sin(a) * d, rr, 0, Math.PI * 2); g.fill();
  }

  // Wet gloss highlight — the "hot asphalt" read.
  g.fillStyle = radial(g, h * 0.66, h * 0.58, R * 0.55, [
    [0.0, 'rgba(255,255,255,0.95)'],
    [0.45, 'rgba(255,255,255,0.32)'],
    [1.0, 'rgba(255,255,255,0)'],
  ]);
  g.beginPath(); g.arc(h * 0.66, h * 0.58, R * 0.55, 0, Math.PI * 2); g.fill();

  g.restore();
}

/**
 * Angular fractured asphalt. The counterpart to `paintChunk`'s water-worn pebble: few sides,
 * high radius variance, and FLAT facets divided by a hard value break rather than a smooth
 * radial gradient, so the silhouette and the shading both read as broken rather than rolled.
 *
 * The shading floor is the same one round 4 fought for in `paintChunk`: the shader
 * MULTIPLIES this by the particle albedo, so a facet darker than ~sRGB 130 times a dark
 * asphalt albedo is RGB 0 on screen whatever the lighting is doing.
 */
function paintShard(g, c, rng, sides, fill) {
  const h = c * 0.5;
  const R = h * 0.92 * fill;
  const px = new Float64Array(sides), py = new Float64Array(sides);
  const a0 = rng.range(0, Math.PI * 2);
  for (let i = 0; i < sides; i++) {
    // Uneven angular spacing is what makes a fracture look like a fracture.
    const a = a0 + (i / sides) * Math.PI * 2 + rng.spread(0.55);
    const r = R * rng.range(0.48, 1.0);
    px[i] = h + Math.cos(a) * r;
    py[i] = h + Math.sin(a) * r;
  }
  g.beginPath();
  g.moveTo(px[0], py[0]);
  for (let i = 1; i < sides; i++) g.lineTo(px[i], py[i]);
  g.closePath();
  g.fillStyle = '#ffffff';
  g.fill();

  g.save();
  g.globalCompositeOperation = 'source-atop';
  // Two flat facets split by a straight cleavage line through the centroid.
  const ca = rng.range(0, Math.PI);
  const nx = Math.cos(ca), ny = Math.sin(ca);
  g.beginPath();
  g.moveTo(h - nx * c, h - ny * c);
  g.lineTo(h + nx * c, h + ny * c);
  g.lineTo(h + nx * c - ny * c, h + ny * c + nx * c);
  g.lineTo(h - nx * c - ny * c, h - ny * c + nx * c);
  g.closePath();
  g.fillStyle = 'rgba(148,148,148,0.92)';
  g.fill();
  // A bright top facet so the shard has a light side and a dark side, hard-edged between.
  g.beginPath();
  g.moveTo(h, h);
  g.lineTo(px[0], py[0]);
  g.lineTo(px[1 % sides], py[1 % sides]);
  g.closePath();
  g.fillStyle = 'rgba(255,255,255,0.85)';
  g.fill();

  // Fine aggregate, so a 40 px shard still has surface.
  const n = 14 + ((rng.next() * 10) | 0);
  for (let i = 0; i < n; i++) {
    const a = rng.range(0, Math.PI * 2);
    const d = Math.sqrt(rng.next()) * R * 0.85;
    const rr = rng.range(c * 0.010, c * 0.032);
    const v = (rng.range(0.6, 1.0) * 255) | 0;
    g.fillStyle = `rgba(${v},${(v * 0.97) | 0},${(v * 0.92) | 0},${rng.range(0.4, 0.9).toFixed(3)})`;
    g.beginPath(); g.arc(h + Math.cos(a) * d, h + Math.sin(a) * d, rr, 0, Math.PI * 2); g.fill();
  }
  g.restore();
}

function paintStreak(g, c) {
  const h = c * 0.5;
  const grd = g.createLinearGradient(0, 0, 0, c);
  grd.addColorStop(0.0, 'rgba(255,255,255,0)');
  grd.addColorStop(0.18, 'rgba(255,255,255,0.55)');
  grd.addColorStop(0.5, 'rgba(255,255,255,1)');
  grd.addColorStop(0.82, 'rgba(255,255,255,0.55)');
  grd.addColorStop(1.0, 'rgba(255,255,255,0)');
  g.save();
  g.globalCompositeOperation = 'source-over';
  g.fillStyle = grd;
  g.fillRect(h - c * 0.10, 0, c * 0.20, c);
  // soften the sides
  g.globalCompositeOperation = 'destination-in';
  const side = g.createLinearGradient(h - c * 0.12, 0, h + c * 0.12, 0);
  side.addColorStop(0.0, 'rgba(0,0,0,0)');
  side.addColorStop(0.5, 'rgba(0,0,0,1)');
  side.addColorStop(1.0, 'rgba(0,0,0,0)');
  g.fillStyle = side;
  g.fillRect(0, 0, c, c);
  g.restore();
}

/**
 * ROUND 12 — THROWN WATER. The `night-rain` critic measured the wheel spray as "a fan of ~30
 * hard-edged white polygon needles with no alpha falloff, no thickness variation and no length
 * variation ... a rolling tyre does not do this", and what they were reading is SPRITE.STREAK.
 * Look at `paintStreak` above and the diagnosis is unavoidable: it is a SYMMETRIC double-taper
 * bar — constant width down its whole middle, both ends identical, and alpha 1.0 across the
 * entire core. Stretched 10:1 by `ParticleLayer.sync` that is a needle, and thirty of them
 * from one emitter is a spark fan.
 *
 * A droplet is not symmetric. It has a heavy leading head and a tail that thins AND fades to
 * nothing behind it. `ParticleLayer.sync` aligns the quad's local +Y with the screen-space
 * velocity, and `_setRect` flips canvas y into texture v, so CANVAS TOP IS THE LEADING END:
 * the head sits at y ~ 0.12c and the tail runs down the cell.
 *
 * Two passes, for two different reasons:
 *   1. a stack of soft radial dabs builds the SILHOUETTE — soft flanks and a real taper, both
 *      of which a hard `fill()` loses the moment the quad is twelve times longer than wide;
 *   2. a `destination-in` gradient down the axis applies the LENGTHWISE alpha falloff, which
 *      is the specific thing the critic said was missing. Pass 1 saturates in the core (that
 *      is what makes the body continuous), so the falloff has to be multiplied in afterwards
 *      rather than painted per dab.
 */
function paintDroplet(g, c, rng) {
  const h = c * 0.5;
  g.save();

  const dabs = 26;
  for (let i = 0; i < dabs; i++) {
    const t = i / (dabs - 1);
    const y = c * (0.12 + 0.84 * t);
    // Widest just behind the head, then a long taper to a point — a shed droplet, not a rod.
    const taper = Math.pow(1 - t, 0.72);
    const bulge = 0.62 + 0.38 * Math.sin(Math.min(1, t * 4.2) * Math.PI * 0.5);
    const r = c * 0.19 * taper * bulge;
    if (r < 0.6) continue;
    // A little wander off the axis. Real thrown water is not a ruled line, and a perfectly
    // straight one is half of why the old sprite read as VFX.
    const x = h + Math.sin(t * 3.1 + 0.6) * c * 0.012 + rng.spread(c * 0.004);
    g.fillStyle = radial(g, x, y, r, [
      [0.0, 'rgba(255,255,255,1)'],
      [0.42, 'rgba(255,255,255,0.92)'],
      [0.78, 'rgba(255,255,255,0.34)'],
      [1.0, 'rgba(255,255,255,0)'],
    ]);
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }

  g.globalCompositeOperation = 'destination-in';
  const grd = g.createLinearGradient(0, 0, 0, c);
  grd.addColorStop(0.00, 'rgba(0,0,0,0)');
  grd.addColorStop(0.10, 'rgba(0,0,0,1)');
  grd.addColorStop(0.30, 'rgba(0,0,0,0.78)');
  grd.addColorStop(0.58, 'rgba(0,0,0,0.38)');
  grd.addColorStop(0.82, 'rgba(0,0,0,0.12)');
  grd.addColorStop(1.00, 'rgba(0,0,0,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, c, c);
  g.restore();
}

/**
 * ROUND 12 — SPARKS. `street-speed`: "the sparks are straight 1-px gold lines with no glow,
 * no bloom, no length variation and no falloff."
 *
 * Three of those four are emitter-side and are fixed in `FX.sparks` / `_doSparks`. GLOW is
 * this file's problem, and it cannot be solved by asking for bloom: `look` owns post (N10)
 * and there is no bloom pass to lean on. So the halo is BAKED — a wide, very soft column
 * under a narrow hot core, drawn on the additive layer, which is what a bloom would have
 * produced anyway and costs nothing per frame.
 *
 * The width ratio is what matters: the core is ~0.15c across and the halo ~0.50c, so at a
 * particle width of 0.05 m the hot line is still a couple of pixels while the glow around it
 * is a visible ~7. `FX.sparks.size` was raised to match — a 0.018 m quad has no room for a
 * halo at all, which is exactly why the old ones read as 1-px lines.
 *
 * Same orientation contract as `paintDroplet`: canvas top is the leading end.
 */
function paintSpark(g, c) {
  const h = c * 0.5;
  g.save();

  // 1. GLOW.
  g.save();
  g.translate(h, c * 0.30);
  g.scale(0.52, 1.0);
  g.fillStyle = radial(g, 0, 0, c * 0.66, [
    [0.0, 'rgba(255,255,255,0.40)'],
    [0.30, 'rgba(255,255,255,0.22)'],
    [0.62, 'rgba(255,255,255,0.085)'],
    [1.0, 'rgba(255,255,255,0)'],
  ]);
  g.beginPath(); g.arc(0, 0, c * 0.66, 0, Math.PI * 2); g.fill();
  g.restore();

  // 2. HOT CORE.
  g.save();
  g.translate(h, c * 0.26);
  g.scale(0.11, 1.0);
  g.fillStyle = radial(g, 0, 0, c * 0.70, [
    [0.0, 'rgba(255,255,255,1)'],
    [0.50, 'rgba(255,255,255,0.95)'],
    [0.85, 'rgba(255,255,255,0.35)'],
    [1.0, 'rgba(255,255,255,0)'],
  ]);
  g.beginPath(); g.arc(0, 0, c * 0.70, 0, Math.PI * 2); g.fill();
  g.restore();

  // 3. NUCLEUS — the incandescent head, so the streak has somewhere it is coming FROM.
  g.fillStyle = radial(g, h, c * 0.15, c * 0.085, [
    [0.0, 'rgba(255,255,255,1)'],
    [0.55, 'rgba(255,255,255,0.80)'],
    [1.0, 'rgba(255,255,255,0)'],
  ]);
  g.beginPath(); g.arc(h, c * 0.15, c * 0.085, 0, Math.PI * 2); g.fill();

  // 4. FALLOFF along travel — a spark cools as it flies and must not end on a hard cut.
  g.globalCompositeOperation = 'destination-in';
  const grd = g.createLinearGradient(0, 0, 0, c);
  grd.addColorStop(0.00, 'rgba(0,0,0,0.42)');
  grd.addColorStop(0.07, 'rgba(0,0,0,1)');
  grd.addColorStop(0.40, 'rgba(0,0,0,0.70)');
  grd.addColorStop(0.75, 'rgba(0,0,0,0.28)');
  grd.addColorStop(1.00, 'rgba(0,0,0,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, c, c);
  g.restore();
}

function paintDrop(g, c) {
  const h = c * 0.5;
  g.save();
  g.beginPath();
  g.moveTo(h, c * 0.06);
  g.quadraticCurveTo(c * 0.84, c * 0.58, h, c * 0.94);
  g.quadraticCurveTo(c * 0.16, c * 0.58, h, c * 0.06);
  g.closePath();
  g.fillStyle = radial(g, h, c * 0.66, c * 0.42, [
    [0.0, 'rgba(255,255,255,1)'],
    [0.6, 'rgba(255,255,255,0.72)'],
    [1.0, 'rgba(255,255,255,0.16)'],
  ]);
  g.fill();
  g.restore();
}

function paintRing(g, c) {
  const h = c * 0.5;
  const grd = radial(g, h, h, h, [
    [0.0, 'rgba(255,255,255,0)'],
    [0.60, 'rgba(255,255,255,0)'],
    [0.78, 'rgba(255,255,255,0.95)'],
    [0.90, 'rgba(255,255,255,0.45)'],
    [1.0, 'rgba(255,255,255,0)'],
  ]);
  g.fillStyle = grd;
  g.beginPath(); g.arc(h, h, h, 0, Math.PI * 2); g.fill();
}

/**
 * Tar spatter on a road.
 *
 * ROUND 6 MAJOR — "the black splat decals are the loudest wrong note in the frame ...
 * flat, pure-black, star/ink-blot sprites from a single shape family". They were: the old
 * silhouette walked `lobes*3` straight segments around a radius modulated by
 * `0.72 + 0.28*sin(a*lobes)`, which at 9 lobes over 27 segments is a nine-pointed STAR, and
 * then three tapered "fingers" were added on top, which is where the asterisk spikes came
 * from. Nothing a bucket of hot mix does to a road looks like that.
 *
 * What §7.1 actually describes is grit and loose stones flung outward: a low, irregular,
 * ROUNDED blot with a ragged but convex-ish edge, a scatter of fine outliers, and a soft
 * fringe. Built from smoothed control points fed through quadratic mid-point curves, so the
 * outline has no straight runs and no spikes at any lobe count.
 *
 * @param {number} lobes  how many control points (higher = busier, still round)
 * @param {number} sats   fine grit outliers
 */
function paintSplat(g, c, rng, lobes, sats) {
  const h = c * 0.5;
  g.fillStyle = '#ffffff';

  // Control ring. `rough` is deliberately small: a blot is irregular, not spiky.
  const n = Math.max(7, lobes);
  const rr = new Float64Array(n);
  for (let i = 0; i < n; i++) rr[i] = rng.range(0.74, 1.0);
  // One smoothing pass kills the high-frequency zigzag that read as points.
  const sm = new Float64Array(n);
  for (let i = 0; i < n; i++) sm[i] = (rr[(i + n - 1) % n] + 2 * rr[i] + rr[(i + 1) % n]) * 0.25;
  const R = h * 0.60;
  const px = new Float64Array(n), py = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    px[i] = h + Math.cos(a) * R * sm[i];
    py[i] = h + Math.sin(a) * R * sm[i] * rng.range(0.92, 1.08);
  }
  // Mid-point quadratic chain: C1-continuous, so there is no corner anywhere on the rim.
  g.beginPath();
  g.moveTo((px[n - 1] + px[0]) * 0.5, (py[n - 1] + py[0]) * 0.5);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    g.quadraticCurveTo(px[i], py[i], (px[i] + px[j]) * 0.5, (py[i] + py[j]) * 0.5);
  }
  g.closePath();
  g.fill();

  // A couple of touching lobes so the blot is not a single ellipse.
  for (let i = 0; i < 3; i++) {
    const a = rng.range(0, Math.PI * 2);
    const d = h * rng.range(0.28, 0.48);
    const r = h * rng.range(0.16, 0.28);
    g.beginPath(); g.arc(h + Math.cos(a) * d, h + Math.sin(a) * d, r, 0, Math.PI * 2); g.fill();
  }

  // Fine grit flung outward — small, round, and never joined to the core.
  for (let i = 0; i < sats; i++) {
    const a = rng.range(0, Math.PI * 2);
    const d = h * rng.range(0.62, 0.92);
    const r = h * rng.range(0.025, 0.075);
    g.globalAlpha = rng.range(0.55, 1.0);
    g.beginPath(); g.arc(h + Math.cos(a) * d, h + Math.sin(a) * d, r, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;

  // Mottling so a decal does not read as a flat sticker.
  g.save();
  g.globalCompositeOperation = 'source-atop';
  for (let i = 0; i < 34; i++) {
    const a = rng.range(0, Math.PI * 2);
    const d = Math.sqrt(rng.next()) * h * 0.72;
    const rad = rng.range(c * 0.02, c * 0.08);
    const v = (rng.range(0.45, 1.0) * 255) | 0;
    g.fillStyle = `rgba(${v},${v},${v},0.5)`;
    g.beginPath(); g.arc(h + Math.cos(a) * d, h + Math.sin(a) * d, rad, 0, Math.PI * 2); g.fill();
  }
  // Feathered rim: tar soaks into the surface, it does not have a cut edge.
  g.globalCompositeOperation = 'destination-in';
  g.fillStyle = radial(g, h, h, h, [
    [0.0, 'rgba(0,0,0,1)'],
    [0.62, 'rgba(0,0,0,1)'],
    [0.88, 'rgba(0,0,0,0.55)'],
    [1.0, 'rgba(0,0,0,0.0)'],
  ]);
  g.fillRect(0, 0, c, c);
  g.restore();
}

/** Longitudinal rubber strip: dark core, striated, feathered along both long edges. */
function paintTyre(g, c, rng) {
  g.save();
  g.fillStyle = 'rgba(255,255,255,0.92)';
  g.fillRect(0, c * 0.16, c, c * 0.68);
  for (let i = 0; i < 16; i++) {
    const y = c * 0.16 + rng.next() * c * 0.68;
    g.fillStyle = `rgba(255,255,255,${rng.range(0.2, 0.7).toFixed(3)})`;
    g.fillRect(0, y, c, c * rng.range(0.012, 0.05));
  }
  g.globalCompositeOperation = 'destination-in';
  const grd = g.createLinearGradient(0, 0, 0, c);
  grd.addColorStop(0.0, 'rgba(0,0,0,0)');
  grd.addColorStop(0.22, 'rgba(0,0,0,0.9)');
  grd.addColorStop(0.5, 'rgba(0,0,0,1)');
  grd.addColorStop(0.78, 'rgba(0,0,0,0.9)');
  grd.addColorStop(1.0, 'rgba(0,0,0,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, c, c);
  // slight taper at the ends so consecutive quads knit instead of banding
  const ends = g.createLinearGradient(0, 0, c, 0);
  ends.addColorStop(0.0, 'rgba(0,0,0,0.72)');
  ends.addColorStop(0.5, 'rgba(0,0,0,1)');
  ends.addColorStop(1.0, 'rgba(0,0,0,0.72)');
  g.fillStyle = ends;
  g.fillRect(0, 0, c, c);
  g.restore();
}

function paintSmudge(g, c, rng) {
  const h = c * 0.5;
  for (let i = 0; i < 12; i++) {
    const cx = h + rng.spread(h * 0.34);
    const cy = h + rng.spread(h * 0.24);
    const r = h * rng.range(0.34, 0.7);
    g.fillStyle = radial(g, cx, cy, r, [
      [0.0, 'rgba(255,255,255,0.34)'],
      [1.0, 'rgba(255,255,255,0)'],
    ]);
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill();
  }
}

function paintDot(g, c) {
  const h = c * 0.5;
  g.fillStyle = radial(g, h, h, h, [
    [0.0, 'rgba(255,255,255,1)'],
    [0.16, 'rgba(255,255,255,1)'],
    [0.34, 'rgba(255,255,255,0.5)'],
    [0.7, 'rgba(255,255,255,0.09)'],
    [1.0, 'rgba(255,255,255,0)'],
  ]);
  g.beginPath(); g.arc(h, h, h, 0, Math.PI * 2); g.fill();
}

/**
 * HARD contact blob. Reference §3.4: "hard-edged, flat, and dark — #222823 under the taxi
 * with NO gradient and NO penumbra ... a graphic anchor, not lighting."
 *
 * That is the whole point of this sprite and the reason the existing `t.decal` blob could
 * not be reused: `decalBlob` is `1 - r^1.7`, which is alpha 1 only at the exact centre — a
 * pure penumbra. Under a truck the centre is hidden by the truck, so all that ever reached
 * the frame was the transparent rim, which is precisely why the measured darkest patch
 * under the hero was 0.793 of the lit road. Here alpha is FLAT 1.0 out to `CORE` and
 * collapses over ~0.1 of the radius, so the visible edge of the blob is still black.
 *
 * ROUND 6: the core moved 0.87 -> FX.contact.coreFrac (0.945). At 0.87 the falloff spanned
 * 0.44 m on a 6.8 m blob, which the near-field softness of the frame turned back into the
 * "soft gradient cloud" four critics reported. §3.4: "no gradient and no penumbra."
 *
 * ROUND 9: the ramp now ENDS at FX.contact.edgeFrac (0.950) instead of at 1.00, because the
 * quad never showed r > 0.977 in the first place — `_setRect` insets every sprite by 1.5 px
 * to stop bilinear bleed, so 0.945 -> 1.00 was truncated at alpha 0.38 and the blob carried
 * four straight rectangular cuts where the ellipse crossed the cell edge. 0.912 -> 0.950
 * completes inside the quad: a genuinely closed, genuinely hard silhouette.
 */
const CONTACT_EDGE = FX.contact.edgeFrac;
function paintContact(g, c, rng) {
  const CONTACT_CORE = FX.contact.coreFrac;
  const WOB = FX.contact.wobble;
  // NOTE: putImageData ignores the canvas transform AND the clip, and the build loop has
  // translated us into this sprite's cell — so the pixels go into a scratch canvas and are
  // blitted with drawImage, which respects both.
  const tmp = document.createElement('canvas');
  tmp.width = c; tmp.height = c;
  const tg = tmp.getContext('2d');
  const img = tg.createImageData(c, c);
  const d = img.data;
  const h = c * 0.5;
  // A little low-frequency wobble on the radius so 600 of these do not read as 600
  // identical ellipses. Amplitude is small — the silhouette must stay a clean graphic.
  const ph = [rng.range(0, 6.28), rng.range(0, 6.28), rng.range(0, 6.28)];
  for (let y = 0; y < c; y++) {
    for (let x = 0; x < c; x++) {
      const dx = (x + 0.5 - h) / h, dy = (y + 0.5 - h) / h;
      const r = Math.sqrt(dx * dx + dy * dy);
      const a2 = Math.atan2(dy, dx);
      const wob = 1 + WOB * (1.4 * Math.sin(a2 * 2 + ph[0]) + 0.9 * Math.sin(a2 * 3 + ph[1])
        + 0.55 * Math.sin(a2 * 5 + ph[2]));
      const core = CONTACT_CORE * wob, edge = CONTACT_EDGE * wob;
      let a;
      if (r <= core) a = 1;
      else if (r >= edge) a = 0;
      else { const t = (r - core) / (edge - core); a = 1 - t * t * (3 - 2 * t); }
      const i = (y * c + x) * 4;
      d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
      d[i + 3] = (a * 255) | 0;
    }
  }
  tg.putImageData(img, 0, 0);
  g.drawImage(tmp, 0, 0);
}

/**
 * VEHICLE FOOTPRINT shadow — round 10.
 *
 * `cone-slalom`: "Ground shadow is a hard-edged ELLIPTICAL blob ... it has a visible oval
 * boundary that cuts across the road and slices through a cone's base."
 *
 * The value was already correct (measured 0.061 of the lit road, reference §3.4 wants
 * ~0.048-0.06). The *shape* was not. §3.4 asks for "a simplified blob roughly matching the
 * car footprint", and a box truck's footprint is a rounded rectangle, not an oval. An oval
 * is also the single most recognisable "this is a decal" tell there is, because no vehicle
 * and no lighting produces one.
 *
 * So: a superellipse |x|^n + |y|^n = 1 at n = `footprintPower` (3.1 — visibly rectangular,
 * still soft enough not to read as a printed rectangle), intersected with a diagonal chamfer
 * so the corners are cut rather than square, with the same low-frequency wobble CONTACT uses
 * so no two instances share a silhouette.
 *
 * The ramp (`footprintCore` → `footprintEdge`) is deliberately ~2x CONTACT's, because the
 * critic asked for 8-12 px of feather at 1920 and CONTACT's 0.038 measured ~6 px on the hero
 * blob. It is still only 3 % of the blob's radius, so §3.4's "no gradient, no penumbra" holds
 * — the flat core is 88 % of the area.
 */
function paintFootprint(g, c, rng) {
  const C = FX.contact;
  const N = C.footprintPower;
  const CH = C.footprintChamfer;
  const CORE = C.footprintCore, EDGE = C.footprintEdge;
  const WOB = C.wobble;
  // Same scratch-canvas dance as paintContact: putImageData ignores transform and clip.
  const tmp = document.createElement('canvas');
  tmp.width = c; tmp.height = c;
  const tg = tmp.getContext('2d');
  const img = tg.createImageData(c, c);
  const d = img.data;
  const h = c * 0.5;
  const ph = [rng.range(0, 6.28), rng.range(0, 6.28), rng.range(0, 6.28)];
  const invN = 1 / N;
  for (let y = 0; y < c; y++) {
    for (let x = 0; x < c; x++) {
      const dx = (x + 0.5 - h) / h, dy = (y + 0.5 - h) / h;
      const ax = Math.abs(dx), ay = Math.abs(dy);
      // Superellipse "radius": the scale factor that would put this pixel on the unit shape.
      let r = Math.pow(Math.pow(ax, N) + Math.pow(ay, N), invN);
      // Diagonal chamfer — an L1 cut that only bites where |x|+|y| is large, i.e. the corners.
      const corner = (ax + ay) * CH;
      if (corner > r) r = corner;
      const a2 = Math.atan2(dy, dx);
      const wob = 1 + WOB * (1.4 * Math.sin(a2 * 2 + ph[0]) + 0.9 * Math.sin(a2 * 3 + ph[1])
        + 0.55 * Math.sin(a2 * 5 + ph[2]));
      const core = CORE * wob, edge = EDGE * wob;
      let a;
      if (r <= core) a = 1;
      else if (r >= edge) a = 0;
      else { const t = (r - core) / (edge - core); a = 1 - t * t * (3 - 2 * t); }
      const i = (y * c + x) * 4;
      d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
      d[i + 3] = (a * 255) | 0;
    }
  }
  tg.putImageData(img, 0, 0);
  g.drawImage(tmp, 0, 0);
}

/**
 * Wheel-spin blur. A disc pinned to the wheel face and oriented along the axle, so a still
 * frame at 87 km/h shows the rim smeared into concentric arcs instead of a pin-sharp hub.
 *
 * ROUND 9 — THE OLD ENVELOPE HAD A HOLE EXACTLY WHERE THE SPOKES ARE.
 *
 * The alpha ramp used to be 0 out to r 0.15 and only reached 1 at r 0.26, on the theory that
 * "the hub cap stays legible". Two things were wrong with that. First, a hub cap turning at
 * 44 rad/s is the LEAST legible thing on the vehicle — smearing it is the whole point.
 * Second, `vehicle`'s rim geometry puts the dish, the five spokes and the lug ring at
 * r 0.33-0.55 of this disc, i.e. straight through the region the ramp was still climbing
 * out of. Cropping `street-speed` at 4x showed all five spoke highlights crisp on all three
 * rear hubs while the blur read only as a pale crescent outside them — a critic could count
 * the spokes at a claimed 87 km/h.
 *
 * Now: opaque from r 0.045 to 0.90, closing at 0.95 (inside the atlas's 1.5 px inset, so the
 * disc is a circle and not a circle with four rectangular bites). Only a 4.5% axle-nut core
 * stays clear.
 *
 * The arcs also carry real VALUE RANGE — 0.12 to 1.0 rather than 0.45 to 1.0 — because the
 * tint multiplies them. A smear of one grey is a plate; a smear that keeps the tyre's dark
 * gaps between the rim's bright streaks is a wheel that is turning.
 */
function paintSpin(g, c, rng) {
  const h = c * 0.5;
  g.save();
  g.translate(h, h);
  g.lineCap = 'butt';

  // Base: the tyre's own dark value, so the disc never lightens the wheel on average.
  g.globalAlpha = 1;
  g.fillStyle = 'rgb(46,45,43)';
  g.beginPath(); g.arc(0, 0, h, 0, Math.PI * 2); g.fill();

  // Concentric arc streaks across the WHOLE face. Each arc is one smeared rim feature.
  for (let i = 0; i < 340; i++) {
    // Bias the sample toward the rim, where the spokes and lug ring actually live.
    const t = rng.next();
    const r = h * (0.05 + 0.87 * Math.sqrt(t));
    const a0 = rng.range(0, Math.PI * 2);
    // A feature at radius r sweeps the same ANGLE regardless of r, so short arcs at the hub
    // and long ones at the rim is backwards — sweep is constant and the arc length grows.
    const sweep = rng.range(0.9, 3.4);
    const v = (rng.range(0.12, 1.0) * 255) | 0;
    g.strokeStyle = `rgb(${v},${(v * 0.99) | 0},${(v * 0.96) | 0})`;
    g.globalAlpha = rng.range(0.30, 0.95);
    g.lineWidth = h * rng.range(0.012, 0.055);
    g.beginPath();
    // ELLIPSES, not circles, each with its own eccentricity and rotation. Perfect concentric
    // arcs pile up into rings, and at low speed `winter-slush` read as a bullseye painted on
    // the hub rather than as a wheel turning. A few percent of eccentricity is invisible on
    // any one arc and kills the ring structure across 340 of them.
    const e = rng.range(-0.07, 0.07);
    g.ellipse(0, 0, r * (1 + e), r * (1 - e), rng.range(0, Math.PI), a0, a0 + sweep);
    g.stroke();
  }

  // Two continuous bands — the lug ring and the tyre shoulder — so the arcs read as one
  // smeared object rather than as scratches. Deliberately faint: they are structure, not
  // graphics, and at the round-9 first pass they were half the bullseye.
  g.globalAlpha = 0.16;
  g.strokeStyle = '#e6e4df';
  g.lineWidth = h * 0.07;
  g.beginPath(); g.ellipse(0, 0, h * 0.47, h * 0.44, 0.4, 0, Math.PI * 2); g.stroke();
  g.globalAlpha = 0.12;
  g.strokeStyle = '#2b2a28';
  g.lineWidth = h * 0.12;
  g.beginPath(); g.ellipse(0, 0, h * 0.78, h * 0.82, 1.1, 0, Math.PI * 2); g.stroke();

  g.globalAlpha = 1;
  // Radial alpha envelope: a 4.5% axle core, then solid to the tyre shoulder.
  g.globalCompositeOperation = 'destination-in';
  g.fillStyle = radial(g, 0, 0, h, [
    [0.000, 'rgba(0,0,0,0)'],
    [0.045, 'rgba(0,0,0,0)'],
    [0.085, 'rgba(0,0,0,1)'],
    [0.900, 'rgba(0,0,0,1)'],
    [0.950, 'rgba(0,0,0,0)'],
    [1.000, 'rgba(0,0,0,0)'],
  ]);
  g.beginPath(); g.arc(0, 0, h, 0, Math.PI * 2); g.fill();
  g.restore();
}

/**
 * Full-frame radial speed streak. Unlike SPEEDLINES (a square whoosh sprite used for the
 * near-miss spike) this cell is mapped 1:1 onto the frame rectangle, so the clear centre
 * below is a clear centre ON SCREEN — the brief's "must not obscure the road" is a
 * geometric guarantee here, not a hope.
 *
 * `CLEAR` is the normalised radius at which streaks are still fully transparent. The road
 * ahead sits inside r ≈ 0.5 at the shot framings, so 0.52 leaves it untouched and the
 * streaking lives in the outer thirds where peripheral motion actually is.
 */
function paintSpeedStreak(g, c, rng) {
  const h = c * 0.5;
  const S = FX.speed;
  const CLEAR = S.streakClear, FULL = S.streakFull;
  g.save();
  g.translate(h, h);
  g.lineCap = 'round';
  g.strokeStyle = '#ffffff';
  /**
   * ROUND 9: 260 fat strokes became 620 fine ones at roughly half the alpha. The cell is
   * 128 px stretched across 1920, so every stroke arrives ~15x magnified; at the old widths
   * that is a 6-21 px soft BAND, which is what "the extreme near field is destroyed" looks
   * like up close. Many thin overlapping strokes integrate to the same energy and read as
   * blur instead of as bars.
   */
  for (let i = 0; i < 620; i++) {
    const a = rng.range(0, Math.PI * 2);
    const r0 = h * rng.range(CLEAR, 1.30);
    // Streak LENGTH grows with radius — the near field smears further than the mid field,
    // which is the gradient the round-6 critic could not find.
    const depth = Math.min(1, Math.max(0, (r0 / h - CLEAR) / (FULL - CLEAR)));
    const r1 = r0 + h * rng.range(0.10, 0.30) * (0.35 + 1.55 * depth);
    g.lineWidth = c * rng.range(0.0018, 0.0062) * (0.55 + 0.9 * depth);
    g.globalAlpha = rng.range(0.16, 0.62);
    g.beginPath();
    g.moveTo(Math.cos(a) * r0, Math.sin(a) * r0);
    g.lineTo(Math.cos(a) * r1, Math.sin(a) * r1);
    g.stroke();
  }
  g.globalAlpha = 1;
  g.globalCompositeOperation = 'destination-in';
  /**
   * The depth ramp. Sampled per-stop rather than left to a two-stop gradient so the
   * `streakRamp` exponent actually shapes the curve: a linear ramp still reads as an edge
   * where it meets the clear zone.
   */
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, h * FULL);
  const STOPS = 10;
  for (let i = 0; i <= STOPS; i++) {
    const t = i / STOPS;                       // 0..1 along the gradient radius
    const r = t * FULL;                        // in half-height units
    const u = r <= CLEAR ? 0 : (r - CLEAR) / (FULL - CLEAR);
    grd.addColorStop(t, `rgba(0,0,0,${Math.pow(u, S.streakRamp).toFixed(4)})`);
  }
  g.fillStyle = grd;
  g.fillRect(-h, -h, c, c);

  /**
   * MEASURED, and the reason a first pass of this overlay looked fine in the abstract and
   * wrecked the frame in practice. A radial clear zone is clear at the CENTRE, but the
   * frame is 16:9: its bottom edge sits at r ~ 0.93, so everything below 60% of the
   * half-height still streaked — and that is exactly where the near road ribbon and the
   * hero's contact shadow are. An additive white overlay there erased the black we had
   * just spent item 1 putting on the ground.
   *
   * Canvas +y is the quad's -Y, which is screen DOWN (the texture is flipY and uv is
   * derived from position.xy + 0.5), so this second bite is taken out of the bottom-centre:
   * the road ribbon stays untouched while the bottom CORNERS still streak.
   */
  g.globalCompositeOperation = 'destination-out';
  const by = h * S.streakRoadBiteY, br = h * S.streakRoadBiteR, bs = S.streakRoadBiteStrength;
  const road = g.createRadialGradient(0, by, 0, 0, by, br);
  road.addColorStop(0.0, `rgba(0,0,0,${bs})`);
  road.addColorStop(0.55, `rgba(0,0,0,${(bs * S.streakRoadBiteInner).toFixed(3)})`);
  road.addColorStop(1.0, 'rgba(0,0,0,0)');
  g.fillStyle = road;
  g.fillRect(-h, -h, c, c);
  g.restore();
}

/** Radial speed lines with a clear centre — the near-miss whoosh overlay. */
function paintSpeedLines(g, c, rng) {
  const h = c * 0.5;
  g.save();
  g.translate(h, h);
  g.strokeStyle = 'rgba(255,255,255,0.9)';
  g.lineCap = 'round';
  for (let i = 0; i < 84; i++) {
    const a = rng.range(0, Math.PI * 2);
    const r0 = h * rng.range(0.34, 0.66);
    const r1 = h * rng.range(0.85, 1.02);
    g.lineWidth = c * rng.range(0.004, 0.016);
    g.globalAlpha = rng.range(0.25, 1.0);
    g.beginPath();
    g.moveTo(Math.cos(a) * r0, Math.sin(a) * r0);
    g.lineTo(Math.cos(a) * r1, Math.sin(a) * r1);
    g.stroke();
  }
  g.globalAlpha = 1;
  // fade the very edge so the overlay has no hard border
  g.globalCompositeOperation = 'destination-in';
  g.fillStyle = radial(g, 0, 0, h, [
    [0.0, 'rgba(0,0,0,1)'],
    [0.88, 'rgba(0,0,0,1)'],
    [1.0, 'rgba(0,0,0,0)'],
  ]);
  g.beginPath(); g.arc(0, 0, h, 0, Math.PI * 2); g.fill();
  g.restore();
}
